import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { Users, Phone, Monitor, Briefcase, Coffee, AlertCircle, X } from 'lucide-react';

// Standalone clock-in page for back-office roles (Accounts / IT / Support /
// Receptionist) who don't work on projects. Provides Start Shift, Activities,
// Breaks (with meal PIN), and End Shift — the same events as the Active
// Projects shift bar, without the task machinery.
const ACTIVITIES = [
  { id: 'meeting', label: 'Meeting', icon: Users },
  { id: 'call', label: 'Call', icon: Phone },
  { id: 'training', label: 'Training', icon: Monitor },
  { id: 'no_jobs', label: 'No Jobs', icon: Briefcase },
];
const BREAK_TYPES = ['lunch', 'snacks', 'dinner'];

const normalizeBreakType = (t) => (t || '').toString().trim().toLowerCase();

// Same break windows as Active Projects.
const isBreakWindowOpen = (type, now = new Date()) => {
  const m = now.getHours() * 60 + now.getMinutes();
  switch (type) {
    case 'lunch':  return m >= 13 * 60 && m <= 15 * 60 + 30;
    case 'snacks': return m >= 18 * 60 && m <= 18 * 60 + 30;
    case 'dinner': return m >= 20 * 60 + 30 && m <= 22 * 60 + 30;
    default: return false;
  }
};

const ClockIn = () => {
  const { token } = useAuth();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [shift, setShift] = useState({ current_status: 'offline' });
  const [error, setError] = useState('');
  const [mealPin, setMealPin] = useState({ open: false, pin: '', meal: '' });

  const fetchShift = useCallback(async () => {
    try {
      const res = await api.get('/user/shift-summary', authHeader);
      setShift(res.data || { current_status: 'offline' });
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    fetchShift();
    const t = setInterval(fetchShift, 60000);
    return () => clearInterval(t);
  }, [fetchShift]);

  const sendEvent = async (event, subType = null) => {
    if (event === 'shift_start' && window.prompt("Type 'start' to confirm starting your shift:") !== 'start') return;
    if (event === 'shift_end' && window.prompt("Type 'end' to confirm ending your shift:") !== 'end') return;
    setError('');
    try {
      const payload = { event };
      if (event === 'break_start' || event === 'break_end') payload.break_type = subType;
      if (event === 'idle_start' || event === 'idle_end') payload.idle_type = subType;
      const res = await api.post('/time/event', payload, authHeader);
      if (event === 'break_start' && res.data?.meal_pin) {
        setMealPin({ open: true, pin: String(res.data.meal_pin), meal: res.data.meal || subType || '' });
      }
      await fetchShift();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update status');
    }
  };

  const status = shift.current_status;
  const activeType = status === 'break' ? normalizeBreakType(shift.current_activity) : null;
  const visibleBreaks = [
    ...(activeType && BREAK_TYPES.includes(activeType) ? [activeType] : []),
    ...BREAK_TYPES.filter((t) => t !== activeType && isBreakWindowOpen(t)),
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Clock In</h1>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-5">
        <h2 className="text-lg font-semibold text-gray-800">
          Shift Status: <span className="capitalize text-blue-600">{status}</span>
        </h2>
        {status !== 'offline' && shift.shift_start && (
          <p className="text-sm text-gray-500 mt-1">{new Date(shift.shift_start).toLocaleString()}</p>
        )}

        {status === 'offline' ? (
          <button
            onClick={() => sendEvent('shift_start')}
            className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >Start Shift</button>
        ) : (
          <div className="mt-4 space-y-5">
            {/* Activities */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Activities</h3>
              <div className="flex flex-wrap gap-2">
                {ACTIVITIES.map((item) => {
                  const isCurrent = status === 'idle' && shift.current_activity === item.id;
                  const otherRunning = status === 'idle' && shift.current_activity && shift.current_activity !== item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => sendEvent(isCurrent ? 'idle_end' : 'idle_start', item.id)}
                      disabled={otherRunning || status === 'break'}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center ${
                        isCurrent ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : (otherRunning || status === 'break') ? 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <item.icon className="w-3 h-3 mr-1.5" /> {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Breaks */}
            {visibleBreaks.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Breaks</h3>
                <div className="flex flex-wrap gap-2">
                  {visibleBreaks.map((type) => {
                    const isCurrent = type === activeType;
                    return (
                      <button
                        key={type}
                        onClick={() => sendEvent(isCurrent ? 'break_end' : 'break_start', type)}
                        disabled={status === 'break' && activeType !== type}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize flex items-center ${
                          isCurrent ? 'bg-orange-100 text-orange-700 border border-orange-200'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <Coffee className="w-3 h-3 mr-1.5" /> {isCurrent ? `Stop ${type}` : type}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* End Shift */}
            <button
              onClick={() => sendEvent('shift_end')}
              className="px-5 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
            >End Shift</button>
          </div>
        )}
      </div>

      {/* Meal PIN dialog */}
      {mealPin.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden text-center">
            <div className="p-6">
              <div className="text-sm font-medium text-gray-500 mb-1">Your {(mealPin.meal || 'meal').replace(/_/g, ' ')} PIN</div>
              <div className="text-5xl font-extrabold tracking-[0.35em] text-blue-700 my-4">{mealPin.pin}</div>
              <p className="text-sm text-gray-600">Show this PIN to the cafeteria staff to collect your meal.</p>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button onClick={() => setMealPin({ open: false, pin: '', meal: '' })}
                className="w-full px-4 py-2 text-white bg-blue-700 rounded-lg hover:bg-blue-800 font-medium">Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClockIn;
